/**
 * A vector for a piece of text, computed here, out of arithmetic.
 *
 * DETERMINISTIC, LOCAL, and there is NO MODEL FILE AND NO NETWORK. Nothing is
 * downloaded the first time this runs, nothing is fetched on a miss, and the same
 * string yields the same `EMBED_DIM` numbers in every runtime this ships to. That
 * last property is not a nicety: `retrieve.ts` caches these vectors under a key,
 * and a cache is only sound if the function under it cannot drift.
 *
 * What it actually is — feature hashing over three kinds of feature:
 *
 *  - WORD UNIGRAMS, so the vector still knows what the text is about;
 *  - WORD BIGRAMS, so "complex one" and "one complex" are not the same evidence;
 *  - CHARACTER TRIGRAMS inside each word, which are the whole reason this exists.
 *    "mitochondrial" and "mitochondria" share every trigram but one, "respiration"
 *    and "respiring" share four, "slows" and "slower" share two. A keyword search
 *    sees three different words and scores zero; this sees a passage saying the
 *    same thing in other words, which is exactly the passage BM25 was missing.
 *
 * Each feature's count is log-scaled (`1 + ln n`, so a word repeated twelve times
 * counts about three and a half times a word said once — a passage does not become
 * more about its subject in proportion to how often it repeats itself), added into
 * its bucket with a SIGN, and the whole vector is L2-normalised so that `cosine`
 * is a plain dot product and length is not similarity.
 *
 * ── why the sign ──────────────────────────────────────────────────────────────
 * Feature hashing collides: a full-size passage yields close to a thousand
 * distinct features, and a thousand features do not fit in 512 buckets however
 * they are hashed. Adding every one as a POSITIVE number makes every collision
 * ADD, so two
 * texts that share nothing still overlap wherever their unrelated features
 * happened to land together. Taking one bit of the same hash as a sign makes a
 * collision cancel as often as it adds, so what is left is noise around zero
 * rather than a floor. Measured on the same six unrelated passages, unsigned
 * against signed: the pairwise cosine between them fell from 0.27 to 0.13 on
 * average and from 0.37 to 0.20 at worst, and a long passage about nothing to do
 * with the claim scored 0.195 against it unsigned and 0.074 signed.
 *
 * That last figure is the whole argument. `SEMANTIC_FLOOR` in `retrieve.ts` is
 * 0.15, so unsigned, an unrelated passage would clear the floor on collision noise
 * alone. A threshold above a similarity that every pair of English paragraphs
 * reaches is not a threshold.
 *
 * ── why 512 ───────────────────────────────────────────────────────────────────
 * The corpus ceiling is 250,000 characters and a passage tops out at 1,400, so a
 * full corpus is a few hundred passages of some hundreds of features each.
 * Against that, 512 is where widening stops paying. Worst-case cosine between two
 * unrelated passages, over the same six: 0.31 at 128 buckets, 0.24 at 256, 0.20 at
 * 512 — and then 0.18 at 1024 and 0.20 at 2048, which is to say it stops falling.
 * What is left at 512 is not collision any more, it is the vocabulary two English
 * passages genuinely share, and no width fixes that.
 *
 * 512 floats is 2 KB a passage, so even a corpus at its ceiling is a megabyte or
 * so of vectors and the cache in `retrieve.ts` never has to choose what to keep.
 * It is a power of two only because that is a tidy size to write down; the bucket
 * is a modulo, not a mask, so nothing here needs it to be.
 *
 * ── why this and not a real model ─────────────────────────────────────────────
 * A sentence-embedding model would be better at this, and the interface here is
 * deliberately the one such a model would be served through: text in, a unit
 * vector out, a version integer that invalidates the cache. Swapping the body of
 * `embed` for a downloaded model — behind an explicit press, with the download
 * shown and refusable — changes nothing else in the tree.
 *
 * It is not what ships first, for the same reason the retrieval does not call an
 * API. A model file is something to download, something to host, something to
 * trust, something that can fail to load, and a second answer to "what left this
 * machine?". The arithmetic version has none of that: it measures about 0.3 ms for
 * a passage at the 1,400-character ceiling, it works offline and on first run, and
 * its behaviour can be read off this file. It buys most of the paraphrase recall
 * for none of the surface area, and it can be replaced the day the download is
 * worth asking for.
 *
 * ── why it does not share `retrieve.ts`'s splitter ────────────────────────────
 * `tokenise` there drops a stoplist and keeps intra-word hyphens and decimal
 * points, because BM25 ranks TERMS and "room-temperature" and "0.2" are terms that
 * carry a claim. This wants the opposite: "room-temperature" split into its two
 * words, so that a passage writing it unhyphenated still lands nearby. It also
 * needs no stoplist, because the sign above already prices the shape of English at
 * zero rather than at a floor. Two splitters, two jobs — and importing that one
 * would put `embed` and `retrieve` in a cycle besides.
 */

import { fnv1a } from './similarity'

/**
 * The width of the vector. See "why 512" above.
 *
 * Exported because a caller that stores or compares these has to be able to say
 * what it is holding, and a test has to be able to check the width it got.
 */
export const EMBED_DIM = 512

/**
 * Bumped whenever anything above the line changes the numbers this returns — the
 * features, the scaling, the width, the hash.
 *
 * It exists so that a cache keyed on it invalidates itself rather than serving a
 * vector computed by a function that no longer exists. There is no migration and
 * nothing to migrate: a vector is derived data, cheap to recompute, and the only
 * wrong answer is a stale one.
 */
export const EMBED_VERSION = 1

/** Characters per character-gram. Three is the shortest that is still a piece of a
 *  word rather than a letter pair every English word contains. */
const GRAM_CHARS = 3
/**
 * The shortest word that gets character-grams at all.
 *
 * A three-letter word's only trigram is the word itself, which the unigram feature
 * already carries — so emitting it would double the weight of short words without
 * adding a single shared feature to any paraphrase. Four is where a word starts
 * having sub-word structure to share.
 */
const GRAM_MIN_WORD = 4

/**
 * Words, for this file's purposes: runs of letters and digits, lowercased.
 *
 * Punctuation is a separator, full stop — which does split "0.074" into two
 * tokens, and that is the accepted cost. The exact-figure arm of the retrieval is
 * BM25, whose splitter keeps the decimal intact; this arm's job is the paraphrase,
 * and it never has to be the thing that finds "0.2 nN".
 */
const wordsOf = (text: string): string[] => text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []

/**
 * Where a feature lands: which bucket, and with which sign.
 *
 * One hash serves both, which is the point — the sign has to be a property OF the
 * feature and stable across calls, or two texts sharing a feature would not share
 * a contribution. The top bit is used for the sign and the low bits for the
 * bucket, so the two are drawn from opposite ends of the same 64.
 *
 * Exported so that a test can place a feature by hand and check the vector it
 * expects, rather than checking `embed` against itself.
 */
export function hashFeature(feature: string): { bucket: number; sign: 1 | -1 } {
  const h = fnv1a(feature)
  return { bucket: Number(h % BigInt(EMBED_DIM)), sign: (h >> 63n) & 1n ? -1 : 1 }
}

/**
 * The feature strings for a text.
 *
 * Namespaced by kind — `w`, `b`, `g` — so that the word "the" and the trigram
 * "the" are two features and not one. Without the prefixes every three-letter
 * word would collide with its own trigram by construction rather than by luck,
 * which is the one collision feature hashing must not have.
 */
function featuresOf(text: string): Map<string, number> {
  const counts = new Map<string, number>()
  const bump = (feature: string) => counts.set(feature, (counts.get(feature) ?? 0) + 1)

  const words = wordsOf(text)
  for (let i = 0; i < words.length; i++) {
    const word = words[i]!
    bump(`w ${word}`)
    if (i + 1 < words.length) bump(`b ${word} ${words[i + 1]!}`)
    if (word.length < GRAM_MIN_WORD) continue
    for (let j = 0; j + GRAM_CHARS <= word.length; j++) bump(`g ${word.slice(j, j + GRAM_CHARS)}`)
  }
  return counts
}

/**
 * The vector. Unit length, or all zeros for a text with no words in it.
 *
 * All zeros is the right answer to "what is this text like?" when there is no
 * text: it makes `cosine` return 0 against everything, so empty text is similar to
 * NOTHING. The alternative — leaving it un-normalised, or seeding a bias term —
 * would make the empty string mildly similar to everything, which is the failure
 * this whole layer exists downstream of.
 */
export function embed(text: string): Float32Array {
  const vector = new Float32Array(EMBED_DIM)
  for (const [feature, count] of featuresOf(text)) {
    const { bucket, sign } = hashFeature(feature)
    vector[bucket]! += sign * (1 + Math.log(count))
  }

  let sumSquares = 0
  for (const value of vector) sumSquares += value * value
  const norm = Math.sqrt(sumSquares)
  if (norm > 0) for (let i = 0; i < EMBED_DIM; i++) vector[i]! /= norm
  return vector
}

/**
 * Cosine similarity of two vectors from `embed`, in −1…1.
 *
 * A plain dot product, because both sides are already unit length. Two vectors of
 * different widths score 0 rather than throwing or comparing a prefix: a width
 * mismatch means one of them was produced by a version of `embed` that no longer
 * exists, and the honest answer to "how similar are these?" is then "no answer" —
 * which has to be the number that admits nothing, never a high one.
 */
export function cosine(a: Float32Array, b: Float32Array): number {
  if (a.length !== b.length) return 0
  let sum = 0
  for (let i = 0; i < a.length; i++) sum += a[i]! * b[i]!
  return sum
}
